import { SYNC_WS_PATH, syncDocNameSchema } from '@awwyeah/shared';
import { IndexeddbPersistence } from 'y-indexeddb';
import { WebsocketProvider } from 'y-websocket';
import * as Y from 'yjs';
import { SyncStatusStore } from './status';
import type {
  AwarenessLike,
  LocalPersistenceLike,
  PersistenceFactory,
  ProviderFactory,
  SyncProviderLike,
} from './types';

/**
 * How long a doc with no holders stays open before it is torn down. Long enough that
 * navigating away and back is free, short enough that we do not hold sockets for screens
 * nobody is looking at.
 */
const DEFAULT_RELEASE_DELAY_MS = 15_000;

/**
 * Reconnect tuning. y-websocket already does exponential backoff with jitter; we cap it at
 * 2.5s because requirement #3 is "visible to the other shopper within ~1s when any
 * connectivity exists" — a 30s backoff after a tunnel would be user-visible failure.
 * `resyncInterval` is belt-and-braces against a silently dropped update.
 */
export const MAX_BACKOFF_MS = 2_500;
export const RESYNC_INTERVAL_MS = 30_000;

export interface DocHandle {
  name: string;
  doc: Y.Doc;
  /** Resolves once IndexedDB has loaded (offline-first: local data before network). */
  whenLocalLoaded: Promise<void>;
  /** Idempotent. */
  release(): void;
}

interface DocEntry {
  name: string;
  doc: Y.Doc;
  provider: SyncProviderLike | null;
  persistence: LocalPersistenceLike | null;
  whenLocalLoaded: Promise<void>;
  refCount: number;
  teardownTimer: ReturnType<typeof setTimeout> | null;
  detach: (() => void) | null;
}

export interface DocManagerOptions {
  /** Absolute ws(s) URL of the sync hub. Defaults to the current origin + `/sync`. */
  serverUrl?: string;
  createProvider?: ProviderFactory;
  createPersistence?: PersistenceFactory;
  releaseDelayMs?: number;
}

/** `wss://host/sync` in production, `ws://host/sync` in dev — always same-origin. */
export function defaultSyncServerUrl(): string {
  if (typeof window === 'undefined') return `ws://127.0.0.1:3010${SYNC_WS_PATH}`;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}${SYNC_WS_PATH}`;
}

/**
 * Opens Yjs docs by name, one instance per name, with local IndexedDB persistence and a
 * websocket provider to the hub. Reference-counted: the last release schedules teardown,
 * a re-acquire before the timer fires cancels it.
 *
 * Everything the UI touches is local-first — edits apply to the in-memory doc immediately,
 * are persisted to IndexedDB, and are replayed to the server whenever the socket returns.
 */
export class DocManager {
  readonly status: SyncStatusStore;
  private readonly entries = new Map<string, DocEntry>();
  private readonly serverUrl: string;
  private readonly createProvider: ProviderFactory;
  private readonly createPersistence: PersistenceFactory;
  private readonly releaseDelayMs: number;
  private destroyed = false;

  constructor(options: DocManagerOptions = {}) {
    this.serverUrl = options.serverUrl ?? defaultSyncServerUrl();
    this.releaseDelayMs = options.releaseDelayMs ?? DEFAULT_RELEASE_DELAY_MS;
    this.createProvider = options.createProvider ?? this.defaultProvider;
    this.createPersistence = options.createPersistence ?? defaultPersistence;
    this.status = new SyncStatusStore();
    this.status.listenToNetwork();
    if (typeof window !== 'undefined') {
      // Don't wait out the backoff after a tunnel/airplane mode: reconnect immediately.
      window.addEventListener('online', this.reconnectAll);
    }
  }

  /**
   * The `Y.Doc` for `name`, created on first use. Safe to call during render: it never
   * starts network activity. Call `acquire` (from an effect) to keep it synced.
   */
  doc(name: string): Y.Doc {
    return this.entry(name).doc;
  }

  /** Opens (or re-uses) the doc and keeps it synced until the handle is released. */
  acquire(name: string): DocHandle {
    const entry = this.entry(name);
    entry.refCount += 1;
    if (entry.teardownTimer) {
      clearTimeout(entry.teardownTimer);
      entry.teardownTimer = null;
    }
    this.connect(entry);

    let released = false;
    return {
      name,
      doc: entry.doc,
      whenLocalLoaded: entry.whenLocalLoaded,
      release: () => {
        if (released) return;
        released = true;
        entry.refCount = Math.max(0, entry.refCount - 1);
        if (entry.refCount === 0) this.scheduleTeardown(entry);
      },
    };
  }

  /** Doc names currently open (diagnostics/tests). */
  openDocNames(): string[] {
    return [...this.entries.keys()];
  }

  /**
   * The doc's awareness channel, once a provider exists for it (i.e. after `acquire`'s
   * `connect()` has run) — `null` before that or when the transport doesn't support one
   * (a fake in a unit test). Additive-only accessor for WS-16's presence dots; nothing
   * about doc lifecycle changes because of it.
   */
  awareness(name: string): AwarenessLike | null {
    return this.entries.get(name)?.provider?.awareness ?? null;
  }

  refCount(name: string): number {
    return this.entries.get(name)?.refCount ?? 0;
  }

  /** Reconnects every provider now (used on the `online` event). */
  reconnectAll = (): void => {
    for (const entry of this.entries.values()) entry.provider?.connect();
  };

  /**
   * Drops every socket while keeping the docs live and editable — "airplane mode" for the
   * dev page and the WS-8 torture suite. Edits keep queueing locally until `reconnectAll`.
   */
  disconnectAll = (): void => {
    for (const entry of this.entries.values()) entry.provider?.disconnect();
  };

  destroy(): void {
    this.destroyed = true;
    if (typeof window !== 'undefined') window.removeEventListener('online', this.reconnectAll);
    for (const entry of [...this.entries.values()]) this.teardown(entry);
    this.status.destroy();
  }

  private entry(name: string): DocEntry {
    const parsed = syncDocNameSchema.safeParse(name);
    if (!parsed.success) throw new Error(`invalid Yjs doc name: ${name}`);

    const existing = this.entries.get(name);
    if (existing) return existing;

    const doc = new Y.Doc({ gc: true });
    const persistence = this.createPersistence(name, doc);
    const entry: DocEntry = {
      name,
      doc,
      persistence,
      provider: null,
      whenLocalLoaded: persistence
        ? persistence.whenSynced.then(() => undefined)
        : Promise.resolve(),
      refCount: 0,
      teardownTimer: null,
      detach: null,
    };
    this.entries.set(name, entry);
    // An entry created by `doc()` alone is an orphan unless someone acquires it shortly.
    this.scheduleTeardown(entry);
    return entry;
  }

  private connect(entry: DocEntry): void {
    if (entry.provider || this.destroyed) return;
    const provider = this.createProvider(entry.name, entry.doc);
    entry.provider = provider;
    if (!provider) return;

    const report = (): void =>
      this.status.report(entry.name, {
        connected: provider.wsconnected,
        synced: provider.synced,
      });
    const onStatus = (): void => report();
    const onSync = (): void => report();
    const onClose = (): void => report();
    provider.on('status', onStatus);
    provider.on('sync', onSync);
    provider.on('connection-close', onClose);
    entry.detach = () => {
      provider.off('status', onStatus);
      provider.off('sync', onSync);
      provider.off('connection-close', onClose);
    };
    report();
  }

  private scheduleTeardown(entry: DocEntry): void {
    if (entry.teardownTimer) clearTimeout(entry.teardownTimer);
    entry.teardownTimer = setTimeout(() => {
      if (entry.refCount === 0) this.teardown(entry);
    }, this.releaseDelayMs);
  }

  private teardown(entry: DocEntry): void {
    if (entry.teardownTimer) clearTimeout(entry.teardownTimer);
    entry.detach?.();
    entry.provider?.destroy();
    void entry.persistence?.destroy();
    entry.doc.destroy();
    this.entries.delete(entry.name);
    this.status.forget(entry.name);
  }

  private defaultProvider: ProviderFactory = (docName, doc) =>
    new WebsocketProvider(this.serverUrl, docName, doc, {
      maxBackoffTime: MAX_BACKOFF_MS,
      resyncInterval: RESYNC_INTERVAL_MS,
    }) as unknown as SyncProviderLike;
}

const defaultPersistence: PersistenceFactory = (docName, doc) => {
  if (typeof indexedDB === 'undefined') return null;
  return new IndexeddbPersistence(`awwyeah:${docName}`, doc);
};
